import React, { useState, useMemo } from 'react';
import { useEntitlements } from '../hooks/useEntitlements';
import { useAuth } from '../AuthContext';
import { PageSkeleton, LoadingButtonContent } from './LoadingComponents';
import { useToast } from '../contexts/ToastContext';
import { createAuthError, parseErrorMessage } from '../utils/errorHandling';
import { getOspEntitlementStatus, OspEntitlementStatus } from '../services/ospFirebaseService';
import { isBetaTester } from '../config/featureFlags';
import NewPricingScreen from './NewPricingScreen';
import TrialTimelineScreen from './TrialTimelineScreen';
import PlanSelectionScreen from './PlanSelectionScreen';
import { Sparkles, X, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// NEW PRICING CONSTANTS
const PRICE_ULTIMATE_MONTHLY = 'price_1T16vuCR1GWecDhImvID4cxK';
const PRICE_ULTIMATE_QUARTERLY = 'price_1T16ysCR1GWecDhIq2roQwNg';
// Legacy Price IDs
const PRICE_LEGACY_MONTHLY = 'price_1T1bdVCR1GWecDhIHCqyQzEz'; // 149 CZK / month
const PRICE_LEGACY_QUARTERLY = 'price_1T1bdJCR1GWecDhIzLQRECmg'; // 297 CZK / 3 months

interface UpgradePageProps {
  onNavigate?: (section: string) => void;
}

const UpgradePage: React.FC<UpgradePageProps> = ({ onNavigate }) => {
  const { user, userDoc, hasPro, hasPlus, loading, error: entitlementsError, retry } = useEntitlements();
  const { signInWithGoogle } = useAuth();
  const { showError, showSuccess, showInfo } = useToast();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'zsv' | 'osp'>('zsv');
  const [showNewDesign, setShowNewDesign] = useState(true); // Default to new Duolingo-style design
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1); // Multi-step pricing flow
  const [direction, setDirection] = useState(0);

  const isOspPro = React.useMemo(() => {
    if (!userDoc?.ospAiProActiveUntil) return false;
    return userDoc.ospAiProActiveUntil.toMillis() > Date.now();
  }, [userDoc]);

  // Get OSP entitlement status
  const ospEntitlement = useMemo(() => getOspEntitlementStatus(userDoc as any), [userDoc]);

  // Determine if user has already used the trial
  const isPostTrialUser = ospEntitlement.status === OspEntitlementStatus.TRIAL_NOT_ELIGIBLE;

  // Determine if user has a past due or unpaid subscription
  const isPastDueUser = userDoc?.ospSubscriptionStatus === 'past_due' || userDoc?.ospSubscriptionStatus === 'unpaid';

  // Determine if user is a beta tester
  const isBetaUser = user?.email ? isBetaTester(user.email) : false;

  // Handle checkout flow with enhanced error handling
  const handleCheckout = async (priceId: string, productId: 'pro' | 'plus' | 'osp_ai_pro', mode: 'payment' | 'subscription' = 'payment', additionalData: any = {}) => {
    if (!user) {
      // Prompt sign-in
      try {
        showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
        await signInWithGoogle();
        showSuccess('Přihlášení úspěšné!', 'Nyní můžeš pokračovat k nákupu.');
        return;
      } catch (error) {
        console.error('Sign-in failed:', error);
        const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
        setError(errorInfo.message);
        showError(errorInfo.title, errorInfo.message);
        return;
      }
    }

    setCheckoutLoading(true);
    setError(null);

    try {

      // Get Firebase ID token
      const idToken = await user.getIdToken();

      // Make checkout API call
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          priceId: priceId,
          productId: productId,
          mode: mode,
          ...additionalData
        })
      });

      if (!response.ok) {
        throw new Error('Checkout failed');
      }

      const data = await response.json();



      setTimeout(() => {
        window.location.href = data.url;
      }, 1000);

    } catch (error) {
      console.error('Checkout error:', error);
      const errorInfo = parseErrorMessage(error);
      setError(errorInfo.message);
      showError(errorInfo.title, errorInfo.message);
      setCheckoutLoading(false);
    }
  };

  // Handle navigation back to dashboard
  const handleBackToDashboard = () => {
    if (onNavigate) {
      onNavigate('home');
    }
  };

  // Handle try free button - go to step 2 (trial timeline) or skip to 3 if post-trial
  const handleTryFree = async () => {
    if (isPastDueUser && user) {
      setCheckoutLoading(true);
      try {
        const token = await user.getIdToken();
        const response = await fetch('/.netlify/functions/create-portal-session', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error(data.error || 'Failed to create portal session');
        }
      } catch (error) {
        console.error('Portal error:', error);
        showError('Chyba', 'Nepodařilo se otevřít správu předplatného.');
        setCheckoutLoading(false);
      }
      return;
    }

    setDirection(1);
    if (isPostTrialUser) {
      setCurrentStep(3); // Skip timeline, go directly to plan selection
    } else {
      setCurrentStep(2);
    }
  };

  // Handle start trial - go to step 3 (plan selection)
  const handleStartTrial = () => {
    setDirection(1);
    setCurrentStep(3);

  };

  // Handle close/skip
  const handleClosePricing = () => {
    handleBackToDashboard();
  };

  // Handle legacy pricing toggle
  const handleLegacyPricing = () => {
    setShowNewDesign(false);
  };

  // Determine if user is "Legacy" (has Plus or Pro ZSV lifetime purchased)
  const isLegacyUser = userDoc?.role === 'plus' ||
    !!userDoc?.prePremiumRole ||
    (userDoc?.role === 'pro' && userDoc?.entitlements?.pro?.status === 'active' && userDoc?.entitlements?.pro?.productId !== 'ultimate');

  // Handle new pricing toggle from legacy view
  const handleNewPricing = () => {
    setShowNewDesign(true);
  };

  // Handle back navigation in multi-step flow
  const handleBackStep = () => {
    if (currentStep > 1) {
      setDirection(-1);
      setCurrentStep((prev) => (prev - 1) as 1 | 2 | 3);
    }
  };

  // Show loading skeleton while entitlements are loading
  if (loading) {
    return <PageSkeleton />;
  }

  // If user already has Pro, show success state (only if active tab is ZSV, or maybe we want a unified view?)
  // Actually, users might want to buy OSP even if they have ZSV Pro.
  // So we should NOT block the whole page if they have Pro. We should only start showing "You have Pro" for the specific card or section.
  // However, the original code had a full page block. Let's keep the full page block ONLY if they have EVERYTHING?
  // Or better, let's remove the full page block so they can see the other offering.
  // But for now, let's stick to the current logic: 
  // If they have ZSV Pro, the ZSV tab should probably show "You own this".
  // Let's modify the render logic below instead of early return.

  // Show new Duolingo-style pricing flow
  if (showNewDesign) {
    const variants = {
      enter: (direction: number) => ({
        x: direction > 0 ? 50 : -50,
        opacity: 0
      }),
      center: {
        x: 0,
        opacity: 1
      },
      exit: (direction: number) => ({
        x: direction < 0 ? 50 : -50,
        opacity: 0
      })
    };

    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden">
        {/* Shared Static Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/10 rounded-full blur-3xl" />
        </div>

        {/* Static Close Button */}
        <button
          onClick={handleClosePricing}
          className="absolute top-6 right-6 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all duration-200 backdrop-blur-sm"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Static Back Button (only for steps > 1) */}
        {currentStep > 1 && (
          <button
            onClick={handleBackStep}
            className="absolute top-6 left-6 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all duration-200 backdrop-blur-sm"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Animated Content Wrapper */}
        <AnimatePresence mode="popLayout" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30, duration: 0.4 },
              opacity: { duration: 0.2 }
            }}
            className="w-full flex justify-center absolute inset-0 items-center pointer-events-none"
          >
            <div className="w-full pointer-events-auto flex justify-center">
              {currentStep === 1 && (
                <NewPricingScreen
                  onTryFree={handleTryFree}
                  onClose={handleClosePricing}
                  onLegacyPricing={handleLegacyPricing}
                  isLegacyUser={isLegacyUser}
                  isPostTrialUser={isPostTrialUser}
                  isPastDueUser={isPastDueUser}
                  isBetaUser={isBetaUser}
                />
              )}

              {currentStep === 2 && (
                <TrialTimelineScreen
                  onStartTrial={handleStartTrial}
                  onClose={handleClosePricing}
                  onBack={handleBackStep}
                />
              )}

              {currentStep === 3 && (
                <PlanSelectionScreen
                  isLoading={checkoutLoading}
                  onSelectPlan={(plan) => {
                    let priceId;
                    if (isLegacyUser) {
                      priceId = plan === 'monthly'
                        ? PRICE_LEGACY_MONTHLY
                        : PRICE_LEGACY_QUARTERLY;
                    } else {
                      priceId = plan === 'monthly'
                        ? PRICE_ULTIMATE_MONTHLY
                        : PRICE_ULTIMATE_QUARTERLY;
                    }

                    // Call checkout with 'ultimate' product ID
                    // If isPostTrialUser, force isTrial: false (immediate billing)
                    // Otherwise isTrial: true (3-day trial)
                    handleCheckout(priceId, 'ultimate' as any, 'subscription', {
                      planType: plan,
                      isTrial: !isPostTrialUser
                    });
                  }}
                  onClose={handleClosePricing}
                  onBack={handleBackStep}
                  isLegacyUser={isLegacyUser}
                  isPostTrialUser={isPostTrialUser}
                />
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Vyber si svůj plán
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400">
          Urychli své studium s prémiovými funkcemi
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-10">
        <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-xl inline-flex relative">
          {/* Active Tab Indicator Animation - Simple version for now */}
          <button
            onClick={() => setActiveTab('zsv')}
            className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${activeTab === 'zsv'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50'
              }`}
          >
            Společenské vědy (ZSV)
          </button>
          <button
            onClick={() => setActiveTab('osp')}
            className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 flex items-center gap-2 ${activeTab === 'osp'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50'
              }`}
          >
            OSP (AI Tutor)
            <span className="bg-brand-orange text-white text-[10px] px-1.5 py-0.5 rounded-full">NOVÉ</span>
          </button>
        </div>
      </div>

      {/* Error messages */}
      {(error || entitlementsError) && (
        <div className="max-w-2xl mx-auto mb-8 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm text-center">
          {error || entitlementsError}
          {entitlementsError && (
            <button
              onClick={retry}
              className="ml-2 underline hover:no-underline"
            >
              Zkusit znovu
            </button>
          )}
        </div>
      )}

      {/* Content based on Tab */}
      <div className="max-w-4xl mx-auto">
        {activeTab === 'zsv' ? (
          <div className="grid md:grid-cols-2 gap-8">
            {/* ZSV Plus Card */}
            <div className={`rounded-2xl p-8 border transition-all duration-300 ${hasPlus ? 'bg-green-900/10 border-green-500/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Plus</h2>
                <div className="text-3xl font-bold text-brand-orange mb-1">399 CZK</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Jednorázová platba</div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Všechny Flashcards</span>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Všechny učebnicové kapitoly</span>
                </div>
                <div className="flex items-start opacity-50">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </div>
                  <span className="text-gray-500">Audioknihy</span>
                </div>
                <div className="flex items-start opacity-50">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </div>
                  <span className="text-gray-500">Testy a cvičení</span>
                </div>
              </div>

              {hasPlus || hasPro ? (
                <div className="w-full py-3 rounded-xl bg-green-600/20 text-green-400 text-center font-medium border border-green-600/30">
                  {hasPro ? 'Máte vyšší plán (Pro)' : 'Aktivní plán'}
                </div>
              ) : (
                <button
                  onClick={() => handleCheckout('price_1SYRzTCR1GWecDhIDzvikGj7', 'plus')}
                  disabled={checkoutLoading || loading}
                  className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 flex items-center justify-center border border-white/10"
                >
                  <LoadingButtonContent isLoading={checkoutLoading} loadingText="Otevírám Checkout..." defaultText="Získat Plus" />
                </button>
              )}
            </div>

            {/* ZSV Pro Card */}
            <div className={`rounded-2xl p-8 border relative overflow-hidden ${hasPro ? 'bg-green-900/10 border-green-500/30' : 'border-brand-orange/30 bg-brand-orange/5'}`}>
              {!hasPro && (
                <div className="absolute top-0 right-0 bg-brand-orange text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  DOPORUČENO
                </div>
              )}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Pro</h2>
                <div className="text-3xl font-bold text-brand-orange mb-1">
                  {hasPlus ? '300 CZK' : '700 CZK'}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {hasPlus ? 'Doplatek rozdílu ceny' : 'Jednorázová platba'}
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {['Všechny Flashcards', 'Všechny učebnicové kapitoly', 'Audioknihy', 'Testy a cvičení'].map((item) => (
                  <div key={item} className="flex items-start">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              {hasPro ? (
                <div className="w-full py-3 rounded-xl bg-green-600/20 text-green-400 text-center font-medium border border-green-600/30">
                  Aktivní plán
                </div>
              ) : (
                <button
                  onClick={() => handleCheckout(
                    hasPlus ? 'price_1SYS14CR1GWecDhIpueAApV7' : 'price_1S81xhCR1GWecDhI7CNL2ELF',
                    'pro'
                  )}
                  disabled={checkoutLoading || loading}
                  className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white transition-colors duration-200 flex items-center justify-center shadow-lg hover:shadow-brand-orange/25"
                >
                  <LoadingButtonContent isLoading={checkoutLoading} loadingText="Otevírám Checkout..." defaultText={hasPlus ? "Upgrade na Pro" : "Získat Pro"} />
                </button>
              )}
            </div>
          </div>
        ) : (
          // OSP Tab Content
          <div className="max-w-md mx-auto">
            <div className="rounded-2xl p-8 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0F1117] relative overflow-hidden shadow-2xl">
              {/* Effects */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl -mr-20 -mt-20" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl -ml-10 -mb-10" />

              <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
                AI POWERED
              </div>

              <div className="text-center mb-8 relative z-10">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">OSP AI Tutor</h2>
                <div className="text-4xl font-bold text-gray-900 dark:text-white mb-1">
                  444 CZK
                  <span className="text-base font-normal text-gray-500 dark:text-gray-400 ml-1">/ měsíc</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">Předplatné můžeš kdykoliv zrušit</div>
              </div>

              <div className="space-y-4 mb-8 relative z-10">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Osobní mentor 24/7</span>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Masivní denní balíček logických analýz</span>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Sokratovské vedení k řešení</span>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-3 h-3 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Nejvýkonnější AI pro studium OSP testů</span>
                </div>
              </div>

              {isOspPro ? (
                <div className="relative z-10 w-full rounded-xl px-4 py-3.5 font-bold font-poppins bg-green-600/20 text-green-400 text-center border border-green-600/30">
                  Aktivní
                </div>
              ) : ospEntitlement.status === OspEntitlementStatus.UNKNOWN ? (
                // Loading state
                <div className="relative z-10 w-full rounded-xl px-4 py-3.5 font-bold font-poppins bg-muted text-muted-foreground text-center">
                  <LoadingButtonContent isLoading={true} loadingText="Načítání..." defaultText="Načítání..." />
                </div>
              ) : ospEntitlement.status === OspEntitlementStatus.TRIAL_NOT_ELIGIBLE ? (
                // Trial already used - show full subscription CTA
                <button
                  onClick={() => handleCheckout('price_1SkLqYCR1GWecDhITnZoJjNH', 'osp_ai_pro', 'subscription')}
                  disabled={checkoutLoading || loading}
                  className="relative z-10 w-full rounded-xl px-4 py-3.5 font-bold font-poppins bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white transition-all duration-200 flex items-center justify-center shadow-lg shadow-purple-900/40 hover:scale-[1.02]"
                >
                  <LoadingButtonContent isLoading={checkoutLoading} loadingText="Otevírám Checkout..." defaultText="Aktivovat PRO (444 Kč / měsíc)" />
                </button>
              ) : (
                // Trial eligible - show trial CTA
                <>
                  <button
                    onClick={() => handleCheckout('price_1SkLqYCR1GWecDhITnZoJjNH', 'osp_ai_pro', 'subscription', { isTrial: true })}
                    disabled={checkoutLoading || loading}
                    className="relative z-10 w-full rounded-xl px-4 py-3.5 font-bold font-poppins bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white transition-all duration-200 flex items-center justify-center shadow-lg shadow-purple-900/40 hover:scale-[1.02]"
                  >
                    <LoadingButtonContent isLoading={checkoutLoading} loadingText="Otevírám Checkout..." defaultText="Vyzkoušet na 1 den za 1 Kč" />
                  </button>
                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-400">
                      Poté 444 Kč / měsíc. Předplatné můžeš kdykoliv zrušit.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation back */}
      <div className="text-center mt-12 space-y-4">
        <button
          onClick={handleBackToDashboard}
          className="rounded-xl px-4 py-2 font-medium bg-white/10 hover:bg-white/20 text-gray-700 dark:text-gray-300 transition-colors"
        >
          ← Zpět do dashboardu
        </button>

        {/* New Design Toggle */}
        <div className="flex justify-center">
          <button
            onClick={handleNewPricing}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-brand-orange transition-colors underline underline-offset-2"
          >
            <Sparkles className="w-4 h-4" />
            Vyzkoušet nový design
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;